import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLayers, FiAlertCircle, FiDatabase, FiGrid, FiList, FiCopy, FiFileText, FiLayout } = FiIcons;

// Colors for clusters to make it visually distinct
const CLUSTER_COLORS = [
  '#5470c6', '#91cc75', '#fac858', '#ee6666', 
  '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'
];

const PublicElements = () => {
  const [contentTree, setContentTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('visual'); // 'visual' | 'markdown'
  const [copyFeedback, setCopyFeedback] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [clustersRes, elementsRes, questionsRes, relationsRes] = await Promise.all([
          apiService.getClusters(),
          apiService.getElements(),
          apiService.getQuestions(),
          apiService.getClusterElementRelations()
        ]);

        if (clustersRes.status === 'success' && elementsRes.status === 'success') {
          const clusters = clustersRes.data;
          const elements = elementsRes.data;
          const questions = questionsRes.data || [];
          const relations = relationsRes.data || [];

          const tree = clusters.map((cluster, index) => {
            const clusterElementIds = relations
              .filter(r => r.cluster_id === cluster.id)
              .map(r => r.element_id);
            
            const clusterElements = elements
              .filter(e => clusterElementIds.includes(e.id))
              .map(element => {
                const elementQuestions = questions.filter(q => q.element_id === element.id);
                return {
                  ...element,
                  questions: elementQuestions
                };
              });

            return {
              ...cluster,
              elements: clusterElements,
              color: CLUSTER_COLORS[index % CLUSTER_COLORS.length]
            };
          });

          const assignedElementIds = relations.map(r => r.element_id);
          const unassignedElements = elements
            .filter(e => !assignedElementIds.includes(e.id))
            .map(element => ({
              ...element,
              questions: questions.filter(q => q.element_id === element.id)
            }));

          if (unassignedElements.length > 0) {
            tree.push({
              id: 'unassigned',
              title: 'Nicht zugeordnet',
              description: 'Elemente ohne spezifische Cluster-Zuordnung',
              elements: unassignedElements,
              color: '#6b7280'
            });
          }

          setContentTree(tree);
        } else {
          setError('Konnte Datenstruktur nicht laden.');
        }
      } catch (err) {
        console.error('Error fetching content:', err);
        setError('Ein Fehler ist aufgetreten.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const generateMarkdown = () => {
    let md = `# Strukturdaten der Organisationsentwicklung\n\n`;
    md += `Generiert am: ${new Date().toLocaleDateString()}\n\n`;

    contentTree.forEach(cluster => {
      md += `## CLUSTER: ${cluster.title}\n`;
      md += `Beschreibung: ${cluster.description || 'Keine Beschreibung'}\n\n`;
      
      if (cluster.elements.length > 0) {
        cluster.elements.forEach(element => {
          md += `### ELEMENT: ${element.title}\n`;
          md += `Beschreibung: ${element.description || 'Keine Beschreibung'}\n`;
          
          if (element.questions.length > 0) {
            md += `#### Fragen:\n`;
            element.questions.forEach(q => {
              md += `- [ID: ${q.id}] ${q.text}\n`;
            });
          } else {
            md += `_Keine Fragen hinterlegt._\n`;
          }
          md += `\n`;
        });
      } else {
        md += `_Keine Elemente in diesem Cluster._\n\n`;
      }
      md += `---\n\n`;
    });

    return md;
  };

  const handleCopy = () => {
    const md = generateMarkdown();
    navigator.clipboard.writeText(md).then(() => {
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mb-4"></div>
        <p className="text-gray-600">Lade Strukturdaten...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center border-l-4 border-red-500">
          <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Fehler</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "DataCatalog",
    "name": "Strukturdaten der Organisationsentwicklung",
    "description": "Hierarchische Übersicht über Cluster, Elemente und Fragen.",
    "dataset": contentTree.map(cluster => ({
      "@type": "Dataset",
      "name": cluster.title,
      "description": cluster.description,
      "hasPart": cluster.elements.map(element => ({
        "@type": "Dataset",
        "name": element.title,
        "description": element.description,
        "variableMeasured": element.questions.map(q => q.text)
      }))
    }))
  };

  return (
    <div className="min-h-screen bg-white font-sans text-gray-800">
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-[80%] w-full mx-auto px-4 py-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-brand-primary/10 p-2 rounded-lg">
              <SafeIcon icon={FiDatabase} className="w-6 h-6 text-brand-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                Elemente & Fragen
              </h1>
              <p className="text-xs text-gray-500">
                Strukturdaten für LLM-Analyse optimiert
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
             <button
              onClick={() => setViewMode('visual')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'visual' 
                  ? 'bg-white text-brand-primary shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <SafeIcon icon={FiLayout} className="w-4 h-4" />
              Visuell
            </button>
            <button
              onClick={() => setViewMode('markdown')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'markdown' 
                  ? 'bg-white text-brand-primary shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <SafeIcon icon={FiFileText} className="w-4 h-4" />
              Text / LLM
            </button>
            <div className="w-px h-4 bg-gray-300 mx-1"></div>
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-white hover:text-brand-primary transition-all"
              title="Markdown für KI kopieren"
            >
              <SafeIcon icon={FiCopy} className="w-4 h-4" />
              {copyFeedback ? 'Kopiert!' : 'Kopieren'}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[80%] w-full mx-auto px-4 py-8 sm:px-6 lg:px-8">
        
        {viewMode === 'visual' ? (
          // VISUAL MODE
          <div className="space-y-12">
             <div className="prose prose-lg max-w-none text-gray-600 mb-8">
              <p>
                Diese Übersicht stellt die vollständige Struktur der Analyse-Dimensionen dar. 
                Sie ist für die maschinelle Verarbeitung durch LLMs optimiert. Nutzen Sie die 
                <strong>"Text / LLM"</strong> Ansicht für eine reine Textdarstellung oder den 
                <strong>"Kopieren"</strong> Button, um den Kontext zu extrahieren.
              </p>
            </div>

            {contentTree.map((cluster) => (
              <section key={cluster.id} className="scroll-mt-20" id={`cluster-${cluster.id}`}>
                <div 
                  className="flex items-center gap-3 mb-6 pb-2 border-b-2"
                  style={{ borderColor: cluster.color }}
                >
                  <div 
                    className="p-2 rounded-md text-white shadow-sm"
                    style={{ backgroundColor: cluster.color }}
                  >
                    <SafeIcon icon={FiLayers} className="w-6 h-6" />
                  </div>
                  <div>
                    <span 
                      className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded text-white inline-block mb-1"
                      style={{ backgroundColor: cluster.color }}
                    >
                      Cluster
                    </span>
                    <h2 className="text-2xl font-bold text-gray-900 m-0 leading-none">
                      {cluster.title}
                    </h2>
                  </div>
                </div>
                
                {cluster.description && (
                   <p className="text-gray-600 mb-6 italic border-l-4 border-gray-200 pl-4 py-1">
                    {cluster.description}
                  </p>
                )}

                <div className="grid gap-6 md:grid-cols-2">
                  {cluster.elements.length > 0 ? (
                    cluster.elements.map((element) => (
                      <article 
                        key={element.id} 
                        className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full hover:border-brand-primary/30 transition-colors"
                      >
                        <div className="p-5 flex-grow">
                          <div className="flex items-center gap-2 mb-3">
                            <SafeIcon icon={FiGrid} className="w-4 h-4 text-gray-400" />
                            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500">
                              Element
                            </span>
                          </div>
                          <h3 className="text-lg font-bold text-gray-900 mb-2">{element.title}</h3>
                          <p className="text-sm text-gray-600 mb-4 h-auto min-h-[40px]">
                            {element.description}
                          </p>

                          <div className="bg-gray-50 rounded border border-gray-100 p-3">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                              <SafeIcon icon={FiList} className="w-3 h-3" />
                              Fragen ({element.questions.length})
                            </h4>
                            {element.questions.length > 0 ? (
                              <ul className="space-y-2">
                                {element.questions.map((q) => (
                                  <li key={q.id} className="text-gray-700 text-xs leading-relaxed flex items-start gap-2">
                                    <span className="text-gray-400 mt-0.5">•</span>
                                    <span>{q.text}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs text-gray-400 italic">Keine Fragen.</p>
                            )}
                          </div>
                        </div>
                      </article>
                    ))
                  ) : (
                    <div className="col-span-full py-4 text-center text-gray-400 italic bg-gray-50 rounded border border-dashed border-gray-200">
                      Keine Elemente.
                    </div>
                  )}
                </div>
              </section>
            ))}
          </div>
        ) : (
          // MARKDOWN MODE for LLMs
          <div className="bg-gray-900 rounded-xl overflow-hidden shadow-2xl border border-gray-700">
            <div className="bg-gray-800 px-4 py-2 border-b border-gray-700 flex justify-between items-center">
              <span className="text-gray-400 text-xs font-mono">raw_content.md</span>
              <button 
                onClick={handleCopy}
                className="text-xs text-brand-primary hover:text-white transition-colors"
              >
                {copyFeedback ? 'Kopiert!' : 'Alles kopieren'}
              </button>
            </div>
            <pre className="p-6 text-gray-300 font-mono text-sm overflow-x-auto whitespace-pre-wrap leading-relaxed">
              {generateMarkdown()}
            </pre>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 mt-12 py-8">
        <div className="max-w-[80%] w-full mx-auto px-4 text-center text-gray-400 text-xs">
          <p>Optimiert für maschinelle Verarbeitung (LLM/RAG)</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicElements;